import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, Loader2, Send, Eye, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { fetchAnnualReviewSettings, saveAnnualReviewSettings, sendAnnualReviewNow, fetchAnnualReviewPreview } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export const AnnualReviewRow = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["annual-review-settings"], queryFn: fetchAnnualReviewSettings });
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);

  const save = async (payload, ok) => {
    setBusy("save");
    try {
      qc.setQueryData(["annual-review-settings"], await saveAnnualReviewSettings(payload));
      toast.success(ok);
      setEditing(false);
      setEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      qc.invalidateQueries({ queryKey: ["annual-review-settings"] });
    } finally {
      setBusy("");
    }
  };

  const sendNow = async () => {
    setBusy("send");
    try {
      const r = await sendAnnualReviewNow();
      toast.success(`${r.year} check-up (${r.items} items) sent to ${r.sent_to_hint}`);
      qc.invalidateQueries({ queryKey: ["annual-review-settings"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the email — check the Email delivery card below");
    } finally {
      setBusy("");
    }
  };

  const showPreview = async () => {
    setBusy("preview");
    try {
      setPreview(await fetchAnnualReviewPreview());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build preview");
    } finally {
      setBusy("");
    }
  };

  if (!data) return null;
  return (
    <div className="mt-6" data-testid="annual-review-section">
      <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5 mb-2">
        <CalendarCheck className="h-3.5 w-3.5" /> Yearly estate check-up
      </h4>
      <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-sm space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium">One checklist a year: gaps, stale beneficiaries, expiring papers</div>
            <div className="text-xs text-muted-foreground" data-testid="annual-review-status">
              {data.enabled ? `On · next ${data.next_send}` : "Off"}{data.last_sent_at ? ` · last sent ${new Date(data.last_sent_at).toLocaleDateString()}` : ""}
            </div>
          </div>
          <Switch data-testid="annual-review-toggle" checked={data.enabled} disabled={busy === "save"} onCheckedChange={(v) => save({ enabled: v }, v ? "Yearly check-up turned on" : "Yearly check-up turned off")} />
        </div>
        <label className="flex items-center justify-between text-xs gap-2">
          <span className="text-muted-foreground">Send every</span>
          <select data-testid="annual-review-month" value={data.month} disabled={busy === "save"} onChange={(e) => save({ month: Number(e.target.value) }, `Check-up moves to ${MONTHS[e.target.value - 1]}`)} className="h-8 rounded-md border border-border bg-background px-2 text-xs">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        {editing ? (
          <div className="flex items-center gap-2">
            <Input data-testid="annual-review-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="bg-background h-9" autoFocus />
            <Button data-testid="annual-review-email-save-btn" size="sm" onClick={() => save({ email }, "Check-up email saved")} disabled={busy === "save"}>{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        ) : (
          <div className="flex items-center justify-between text-xs">
            <span data-testid="annual-review-email-hint" className={data.email_hint ? "" : "text-muted-foreground"}>
              {data.email_hint ? `Sends to ${data.email_hint}${data.uses_fallback_email ? " (digest / recovery email)" : ""}` : "No email yet — add one to receive the check-up"}
            </span>
            <button data-testid="annual-review-email-edit-btn" onClick={() => setEditing(true)} className="text-primary hover:underline">{data.email_hint ? "Change" : "Add"}</button>
          </div>
        )}
        <div className="flex gap-2 pt-1 flex-wrap">
          <Button asChild data-testid="annual-review-open-link" size="sm" variant="outline" className="h-8 gap-1.5"><Link to="/checkup"><ListChecks className="h-3.5 w-3.5" /> Open checklist</Link></Button>
          <Button data-testid="annual-review-preview-btn" size="sm" variant="outline" className="h-8 gap-1.5" onClick={showPreview} disabled={!!busy}>{busy === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Preview</Button>
          <Button data-testid="annual-review-send-btn" size="sm" variant="outline" className="h-8 gap-1.5" onClick={sendNow} disabled={!!busy || !data.email_hint}>{busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send me one now</Button>
        </div>
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="annual-review-preview-dialog">
          <DialogHeader><DialogTitle className="font-serif">{preview?.data.year} estate check-up · {preview?.data.total_items} items</DialogTitle></DialogHeader>
          {preview && <div className="rounded-xl overflow-hidden border border-border" dangerouslySetInnerHTML={{ __html: preview.html }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
